// Espace du représentant de quartier (lot L5-C1, M17, D-252) : désignations en cours, puis vue du quartier
// (besoins et leur suite, réunions tenues avec compte rendu, engagements, consultations) et expression d'un
// besoin. Seulement dans le quartier et pendant la désignation : l'API répond 404 sinon.
import { CATEGORIES_BESOIN, type CategorieBesoin } from '@cc/contrats';
import { Bandeau, Bouton, Champ, EtatVide, Selection, ZoneTexte } from '@cc/ui';
import { useQueryClient } from '@tanstack/react-query';
import { MapPinned } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { messageErreur } from '../../../api/erreurs';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date, dateHeure } from '../../../commun/format';
import { Bloc, EnTeteCitoyen, ExigeConnexion, Ligne, Liste, TexteStatut } from '../commun';
import { CLES_PARTICIPATION, exprimerBesoin, useMesQuartiers, useQuartierRepresentant } from './api';
import styles from '../citoyen.module.css';

export function PageMesQuartiers() {
  const liste = useMesQuartiers();
  return (
    <>
      <EnTeteCitoyen titre="Mon quartier" sousTitre="Espace des représentants de quartier désignés par la collectivité, pendant leur désignation." />
      <ExigeConnexion titre="Connectez-vous pour accéder à votre espace de représentant">
        <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} fonction="Vos désignations" lignes={2}>
          {(liste.data ?? []).length === 0 ? (
            <EtatVide titre="Aucune désignation en cours" icone={MapPinned}><p>Vous n&apos;êtes représentant d&apos;aucun quartier à ce jour. La désignation est faite par le service de proximité de la collectivité.</p></EtatVide>
          ) : (
            <Liste libelle="Mes quartiers">
              {(liste.data ?? []).map((d) => <Ligne key={d.id} href={`/citoyen/quartiers/${d.quartier.id}`} titre={`Quartier ${d.quartier.nom}`} sousTitre={`${d.collectivite.nom} · du ${date(d.debut)} au ${date(d.fin)}`} />)}
            </Liste>
          )}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}

function FormulaireExpression({ quartier }: { quartier: string }) {
  const client = useQueryClient();
  const [v, setV] = useState({ titre: '', description: '', categorie: 'voirie' as CategorieBesoin, adresse: '' });
  const [etat, setEtat] = useState<{ ok?: string; erreur?: string; enCours?: boolean }>({});
  const envoyer = async () => {
    setEtat({ enCours: true });
    try {
      const r = await exprimerBesoin(quartier, { titre: v.titre, description: v.description, categorie: v.categorie, adresse: v.adresse.trim() || undefined });
      setEtat({ ok: `Besoin ${r.numero} transmis au service de proximité.` });
      setV({ titre: '', description: '', categorie: 'voirie', adresse: '' });
      await client.invalidateQueries({ queryKey: CLES_PARTICIPATION.quartier(quartier) });
    } catch (e) {
      setEtat({ erreur: messageErreur(e) });
    }
  };
  return (
    <Bloc titre="Exprimer un besoin du quartier">
      <form onSubmit={(e) => { e.preventDefault(); void envoyer(); }}>
        <Champ libelle="Titre" value={v.titre} onChange={(e) => setV((x) => ({ ...x, titre: e.target.value }))} required />
        <ZoneTexte libelle="Description" value={v.description} onChange={(e) => setV((x) => ({ ...x, description: e.target.value }))} required />
        <Selection libelle="Catégorie" options={Object.entries(CATEGORIES_BESOIN).map(([valeur, libelle]) => ({ valeur, libelle }))} value={v.categorie} onChange={(e) => setV((x) => ({ ...x, categorie: e.target.value as CategorieBesoin }))} />
        <Champ libelle="Lieu ou repère (facultatif)" value={v.adresse} onChange={(e) => setV((x) => ({ ...x, adresse: e.target.value }))} />
        {etat.ok ? <Bandeau type="succes">{etat.ok}</Bandeau> : null}
        {etat.erreur ? <Bandeau type="erreur">{etat.erreur}</Bandeau> : null}
        <Bouton type="submit" variante="principal" chargement={etat.enCours}>Transmettre le besoin</Bouton>
      </form>
    </Bloc>
  );
}

export function PageQuartierRepresentant() {
  const { id } = useParams();
  const q = useQuartierRepresentant(id);
  const d = q.data;
  return (
    <>
      <EnTeteCitoyen titre={d ? `Quartier ${d.designation.quartier.nom}` : 'Mon quartier'} surTitre={d?.designation.collectivite.nom} sousTitre={d ? `Représentant du ${date(d.designation.debut)} au ${date(d.designation.fin)}` : undefined} retour="/citoyen/quartiers" libelleRetour="Mes quartiers" />
      <ExigeConnexion titre="Connectez-vous pour accéder à votre quartier">
        <EtatRequete chargement={q.isPending} erreur={q.error} onReessayer={() => void q.refetch()} fonction="Le quartier" lignes={4}>
          {d && id ? (
            <>
              <FormulaireExpression quartier={id} />
              <Bloc titre="Besoins du quartier">
                {d.besoins.length === 0 ? <p className={styles.aide}>Aucun besoin exprimé.</p> : (
                  <Liste libelle="Besoins du quartier">
                    {d.besoins.map((b) => (
                      <Ligne key={b.id} titre={<><span className={styles.mono}>{b.numero}</span> · {b.titre}</>} sousTitre={`${b.categorie} · ${b.priorite}${b.de_moi ? ' · exprimé par vous' : ''}${b.orientation ? ` · ${b.orientation.cible === 'signalement' ? 'signalement' : 'projet'} ${b.orientation.numero ?? ''}${b.orientation.etat ? ` (${b.orientation.etat})` : ''}` : ''}`} droite={<TexteStatut libelle={b.etat} ton={b.etat === 'Traité' ? 'ok' : b.etat === 'Non retenu' ? 'attention' : 'info'} />} />
                    ))}
                  </Liste>
                )}
              </Bloc>
              <Bloc titre="Réunions">
                {d.reunions.length === 0 ? <p className={styles.aide}>Aucune réunion.</p> : d.reunions.map((r) => (
                  <div key={r.id}>
                    <p className={styles.aide}>{`${dateHeure(r.date)}, ${r.lieu} : ${r.objet} (${r.etat})`}</p>
                    {r.compte_rendu ? <p className={styles.aide}>{r.compte_rendu}</p> : null}
                  </div>
                ))}
              </Bloc>
              <Bloc titre="Engagements de la collectivité">
                {d.engagements.length === 0 ? <p className={styles.aide}>Aucun engagement.</p> : (
                  <Liste libelle="Engagements">{d.engagements.map((g) => <Ligne key={g.id} titre={g.intitule} sousTitre={`Échéance ${date(g.echeance)}`} droite={<TexteStatut libelle={g.en_retard ? 'Échu' : g.etat} ton={g.en_retard ? 'attention' : g.etat === 'Tenu' ? 'ok' : 'info'} />} />)}</Liste>
                )}
              </Bloc>
              <Bloc titre="Consultations">
                {d.consultations.length === 0 ? <p className={styles.aide}>Aucune consultation publiée.</p> : (
                  <Liste libelle="Consultations du quartier">{d.consultations.map((c) => <Ligne key={c.id} href={`/citoyen/${d.designation.collectivite.code}/consultations/${c.id}`} titre={c.question} sousTitre={`${c.numero} · jusqu'au ${dateHeure(c.fin)}`} />)}</Liste>
                )}
              </Bloc>
            </>
          ) : null}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
